import { read as readXLSX, utils } from "xlsx";
import * as PapaParse from "papaparse";

/**
 * Utilities related to import of CSV & XLSX files
 */

export interface ChannelData {
  channelName: string;
  unit: string;
  value: string;
}

export interface SingleSetDatapoint {
  timestamp: string;
  value: string;
}

export interface ProcessedDatapoint {
  stationId: string;
  timestamp: string;
  channels: ChannelData[];
}

export interface GroupedStationData {
  [stationId: string]: {
    [timestamp: string]: ChannelData[];
  };
}

const numericValuePattern = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

// Mirrors the backend lab value parser: a value is parseable when it is a
// plain number (Finnish decimal comma allowed) optionally prefixed with < or >
export const isParseableLabValue = (rawValue: string): boolean => {
  const trimmedValue = rawValue.trim();

  if (trimmedValue === "" || trimmedValue === "undefined") {
    return false;
  }

  let value = trimmedValue;
  if (value.startsWith("<") || value.startsWith(">")) {
    value = value.substring(1);
  }

  value = value.replace(",", ".");

  return numericValuePattern.test(value);
};

export const parseExcelSheet = (sheet: any): string[][] => {
  const range = utils.decode_range(sheet["!ref"]);
  const data: string[][] = [];

  for (let row = range.s.r; row <= range.e.r; row++) {
    const rowData: string[] = [];
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = utils.encode_cell({ r: row, c: col });
      const cell = sheet[cellAddress];

      let cellValue = "";
      if (cell) {
        if (cell.t === "n") {
          cellValue = cell.v.toString();
        } else if (cell.t === "s") {
          cellValue = cell.v;
        } else if (cell.t === "d") {
          cellValue = cell.w || cell.v.toString();
        } else if (cell.w) {
          cellValue = cell.w;
        } else {
          cellValue = cell.v ? cell.v.toString() : "";
        }
      }

      rowData.push(cellValue);
    }
    data.push(rowData);
  }

  return data;
};

export const excelSerialToUnixTimestamp = (excelSerial: number): number => {
  const excelEpoch = new Date(1899, 11, 30);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const excelDate = new Date(
    excelEpoch.getTime() + excelSerial * millisecondsPerDay,
  );
  return Math.floor(excelDate.getTime() / 1000);
};

export const isExcelSerialDate = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 1 && num < 100000;
};

export const tryConvertTimestamp = (timestamp: string): number | null => {
  const asNumber = parseFloat(timestamp);
  if (!isNaN(asNumber)) {
    if (isExcelSerialDate(timestamp)) {
      return excelSerialToUnixTimestamp(asNumber);
    }
    if (asNumber > 1000000000 && asNumber < 10000000000) {
      return Math.floor(asNumber);
    }
  }

  const parsedDate = new Date(timestamp);
  if (!isNaN(parsedDate.getTime())) {
    return Math.floor(parsedDate.getTime() / 1000);
  }

  return null;
};

export const convertTimestamp = (timestamp: string): number => {
  const converted = tryConvertTimestamp(timestamp);
  if (converted !== null) {
    return converted;
  }

  console.warn("Could not parse timestamp:", timestamp);
  return Math.floor(Date.now() / 1000);
};

export const parseExcelFormat = (data: string[][]): ProcessedDatapoint[] => {
  if (!data || data.length < 3) {
    console.log("Not enough rows in data:", data?.length);
    return [];
  }

  console.log("Parsing data with structure:", {
    totalRows: data.length,
    firstRow: data[0],
    secondRow: data[1],
    thirdRow: data[2],
  });

  const headerRow = data[0];
  const unitRow = data[1];
  const dataRows = data.slice(2);

  console.log("Header row:", headerRow);
  console.log("Unit row:", unitRow);

  const channelColumns: Array<{
    index: number;
    channelName: string;
    unit: string;
  }> = [];

  for (let i = 2; i < headerRow.length; i++) {
    const channelName = headerRow[i];
    const unit = unitRow[i] || "";

    if (
      channelName &&
      channelName.trim() !== "" &&
      !channelName.toLowerCase().includes("timestamp")
    ) {
      channelColumns.push({
        index: i,
        channelName: channelName.trim(),
        unit: unit.trim(),
      });
    }
  }

  console.log("Found channel columns:", channelColumns);

  const processedData: ProcessedDatapoint[] = [];

  for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex++) {
    const row = dataRows[rowIndex];

    if (!row || row.length < 2) {
      console.log(`Skipping row ${rowIndex + 3}: insufficient columns`);
      continue;
    }

    const stationId = row[0]?.toString().trim();
    const rawTimestamp = row[1]?.toString().trim();

    if (!stationId || !rawTimestamp) {
      console.log(
        `Skipping row ${rowIndex + 3}: missing station ID or timestamp`,
        { stationId, rawTimestamp },
      );
      continue;
    }

    const unixTimestamp = convertTimestamp(rawTimestamp);
    const timestamp = unixTimestamp.toString();

    const channels: ChannelData[] = [];

    for (const channel of channelColumns) {
      const rawValue = row[channel.index];

      if (
        rawValue === null ||
        rawValue === undefined ||
        rawValue === "" ||
        rawValue === "undefined"
      ) {
        continue;
      }

      const valueString = rawValue.toString();

      if (isParseableLabValue(valueString)) {
        channels.push({
          channelName: channel.channelName,
          unit: channel.unit,
          value: valueString,
        });
      } else {
        console.log(
          `Skipping unparseable value for ${channel.channelName}:`,
          rawValue,
        );
      }
    }

    if (channels.length > 0) {
      processedData.push({
        stationId,
        timestamp,
        channels,
      });
      console.log(
        `Added datapoint for ${stationId} at ${timestamp} (converted from ${rawTimestamp}) with ${channels.length} channels`,
      );
    } else {
      console.log(`No valid channels found for row ${rowIndex + 3}`);
    }
  }

  console.log("Final processed data:", processedData);
  return processedData;
};

// Parses a two-column file (timestamp, value) for a single datapoint set.
// Header rows are skipped automatically since their timestamp does not parse.
export const parseSingleSetFormat = (
  data: string[][],
): SingleSetDatapoint[] => {
  const datapoints: SingleSetDatapoint[] = [];

  for (const row of data) {
    if (!row) {
      continue;
    }

    const rawTimestamp = (row[0] ?? "").trim();
    const rawValue = (row[1] ?? "").trim();

    if (!rawTimestamp || !rawValue) {
      continue;
    }

    const timestamp = tryConvertTimestamp(rawTimestamp);
    if (timestamp === null) {
      console.log("Skipping row with unparseable timestamp:", rawTimestamp);
      continue;
    }

    if (!isParseableLabValue(rawValue)) {
      console.log("Skipping row with unparseable value:", rawValue);
      continue;
    }

    datapoints.push({
      timestamp: timestamp.toString(),
      value: rawValue,
    });
  }

  return datapoints;
};

export const groupDataForAPI = (
  processedData: ProcessedDatapoint[],
): GroupedStationData => {
  const groupedData: GroupedStationData = {};

  for (const datapoint of processedData) {
    if (!groupedData[datapoint.stationId]) {
      groupedData[datapoint.stationId] = {};
    }

    if (!groupedData[datapoint.stationId][datapoint.timestamp]) {
      groupedData[datapoint.stationId][datapoint.timestamp] = [];
    }

    groupedData[datapoint.stationId][datapoint.timestamp].push(
      ...datapoint.channels,
    );
  }

  return groupedData;
};

export const getTotalDatapointsCount = (
  groupedData: GroupedStationData,
): number => {
  return Object.values(groupedData).reduce((total, station) => {
    return (
      total +
      Object.values(station).reduce((stationTotal, channels) => {
        return stationTotal + channels.length;
      }, 0)
    );
  }, 0);
};

const onFileError = (e: any) => {
  console.error("File parsing error:", e);
};

export const onInputFileChange =
  (
    onFileLoaded: ((data: string[][]) => void) | null,
    onFileContentsAsString: undefined | ((data: string) => void) = undefined,
  ) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    let reader: FileReader = new FileReader();
    const files: FileList = e.target.files!;
    const parserOptions: PapaParse.ParseConfig = {} as PapaParse.ParseConfig;
    const fileEncoding = "UTF-8";

    if (files.length > 0) {
      const file = files[0];
      const isXLSX = file.type.includes("officedocument");

      const parseXLSX = async () => {
        try {
          const fileData = await files[0].arrayBuffer();
          const workbook = readXLSX(fileData);
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

          const data = parseExcelSheet(firstSheet);
          console.log("Parsed XLSX data:", data);
          onFileLoaded?.(data);
        } catch (error) {
          console.error("Error parsing XLSX file:", error);
          onFileError(error);
        }
      };

      reader.onload = (_event: Event) => {
        try {
          if (onFileContentsAsString) {
            onFileContentsAsString(reader.result as string);
          }
          if (!onFileLoaded) {
            return;
          }

          const csvData = PapaParse.parse(
            reader.result as string,
            Object.assign(parserOptions, {
              error: onFileError,
              encoding: fileEncoding,
              header: false,
              skipEmptyLines: false,
            }),
          );

          console.log("Parsed CSV data:", csvData.data);
          onFileLoaded?.(csvData?.data ?? []);
        } catch (error) {
          console.error("Error parsing CSV file:", error);
          onFileError(error);
        }
      };

      if (isXLSX) {
        parseXLSX();
      } else {
        reader.readAsText(files[0], fileEncoding);
      }
    }
  };
